import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Download, Expand, Images, X } from "lucide-react";
import { xShare } from "@/data";

const EASE = [0.22, 1, 0.36, 1];

const MEMES = [
  {
    src: "/memes/meme-rally.jpeg",
    title: "Make Memes Great Again",
    tag: "The Rally Poster",
    rotate: "lg:-rotate-2",
  },
  {
    src: "/memes/meme-right-state.jpeg",
    title: "The Right $TATE of Mind",
    tag: "The Recruitment Poster",
    rotate: "lg:rotate-1",
  },
  {
    src: "/memes/meme-lifes-good.jpeg",
    title: "Life's Good",
    tag: "The Morning Ritual",
    rotate: "lg:-rotate-1",
  },
];

export default function MemeVault() {
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (open === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(null);
      if (e.key === "ArrowRight") setOpen((o) => (o + 1) % MEMES.length);
      if (e.key === "ArrowLeft") setOpen((o) => (o - 1 + MEMES.length) % MEMES.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const active = open !== null ? MEMES[open] : null;

  return (
    <section id="memes" className="relative bg-[#0A0C10] py-28 sm:py-36" data-testid="meme-vault-section">
      <div className="pointer-events-none absolute -right-32 top-20 h-[420px] w-[420px] rounded-full bg-[#E5A93C] opacity-[0.06] blur-[130px]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="text-center"
        >
          <p className="flex items-center justify-center gap-2.5 font-mono2 text-xs uppercase tracking-[0.3em] text-[#E5A93C]">
            <Images className="h-4 w-4" />
            08 / The Meme Vault
          </p>
          <h2 className="mx-auto mt-5 max-w-2xl font-display text-2xl font-extrabold uppercase leading-tight tracking-tight sm:text-3xl lg:text-4xl">
            Fresh from the <span className="text-stroke-gold">propaganda office.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-[#94A3B8] sm:text-base">
            Official movement artwork. Tap any poster to view it full-size, post it to X, or
            download it for the timeline.
          </p>
        </motion.div>

        <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          {MEMES.map((m, i) => (
            <motion.button
              key={m.src}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.75, ease: EASE, delay: i * 0.1 }}
              whileHover={{ y: -8, rotate: 0 }}
              onClick={() => setOpen(i)}
              className={`group relative overflow-hidden rounded-2xl border border-[rgba(229,169,60,0.3)] bg-[#0E1015] text-left shadow-[0_20px_60px_rgba(0,0,0,0.5)] transition-colors duration-300 hover:border-[#F6C85F] ${m.rotate}`}
              data-testid={`meme-card-${i + 1}`}
            >
              <img
                src={m.src}
                alt={`$TATE meme — ${m.title}`}
                loading="lazy"
                className="aspect-square w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#07080A] via-transparent to-transparent opacity-80" />
              <div className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-[rgba(229,169,60,0.5)] bg-[#07080A]/80 opacity-0 backdrop-blur transition-opacity duration-300 group-hover:opacity-100">
                <Expand className="h-4 w-4 text-[#F6C85F]" />
              </div>
              <div className="absolute inset-x-0 bottom-0 p-5">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#F6C85F]">
                  {m.tag}
                </p>
                <p className="mt-1 font-display text-lg font-extrabold uppercase tracking-tight text-[#F8FAFC]">
                  {m.title}
                </p>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-[#07080A]/92 p-4 backdrop-blur-md sm:p-10"
            onClick={() => setOpen(null)}
            data-testid="meme-lightbox"
          >
            <button
              onClick={() => setOpen(null)}
              className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-[rgba(229,169,60,0.5)] bg-[#0E1015] text-[#F6C85F] transition-colors hover:bg-[#E5A93C] hover:text-[#07080A]"
              data-testid="meme-lightbox-close"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setOpen((open - 1 + MEMES.length) % MEMES.length);
              }}
              className="absolute left-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-[rgba(229,169,60,0.5)] bg-[#0E1015] text-[#F6C85F] transition-colors hover:bg-[#E5A93C] hover:text-[#07080A] sm:left-8"
              data-testid="meme-lightbox-prev"
              aria-label="Previous meme"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setOpen((open + 1) % MEMES.length);
              }}
              className="absolute right-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-[rgba(229,169,60,0.5)] bg-[#0E1015] text-[#F6C85F] transition-colors hover:bg-[#E5A93C] hover:text-[#07080A] sm:right-8"
              data-testid="meme-lightbox-next"
              aria-label="Next meme"
            >
              <ChevronRight className="h-5 w-5" />
            </button>

            <motion.div
              key={active.src}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, ease: EASE }}
              className="flex max-h-full flex-col items-center gap-5"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={active.src}
                alt={`$TATE meme — ${active.title}`}
                className="max-h-[68vh] max-w-full rounded-xl border border-[rgba(229,169,60,0.4)] object-contain shadow-[0_0_80px_rgba(229,169,60,0.15)]"
              />
              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  onClick={() => xShare(`${active.title} — $TATE. MAKE MEMES GREAT AGAIN. 🇺🇸🐈`)}
                  className="flex items-center gap-2 rounded-full bg-[#E5A93C] px-6 py-3 font-display text-sm font-bold text-[#07080A] transition-all hover:bg-[#F6C85F]"
                  data-testid="meme-lightbox-share"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                  Post to X
                </button>
                <a
                  href={active.src}
                  download
                  className="flex items-center gap-2 rounded-full border border-[rgba(229,169,60,0.5)] px-6 py-3 font-display text-sm font-bold text-[#F8FAFC] transition-colors hover:border-[#F6C85F] hover:bg-[#0E1015]"
                  data-testid="meme-lightbox-download"
                >
                  <Download className="h-4 w-4 text-[#F6C85F]" />
                  Download
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
